import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Copy } from "lucide-react";
import { toast } from "@/lib/toast";
import api from "@/lib/api";

/**
 * DuplicateTypeDialog — spec rev142 (2026-02-06).
 *
 * Modale de confirmation & renommage pour la duplication d'un type de
 * document. Ouverte au clic sur l'icône « Dupliquer » d'une ligne du
 * tableau Types de documents.
 *
 * Règles UX imposées :
 *  - Titre : "Dupliquer '[nom du type]'"
 *  - Explication courte : "Un nouveau type sera créé avec la même
 *    configuration (étapes, champs, délais)."
 *  - Champ « Nom du nouveau type » prérempli avec « [nom] (copie) ».
 *  - Le bouton « Dupliquer » reste DÉSACTIVÉ tant que le nom n'a pas
 *    été modifié (doit être différent du nom prérempli par défaut ET
 *    non vide).
 *  - Boutons Annuler / Dupliquer.
 */
export default function DuplicateTypeDialog({ open, onOpenChange, type, onDuplicated }) {
  const defaultName = type ? `${type.name} (copie)` : "";
  const [name, setName] = useState(defaultName);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // À chaque nouvelle ouverture (ou changement de type ciblé),
    // ré-initialise le champ au nom par défaut.
    if (open) setName(defaultName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, type?.id]);

  const trimmed = name.trim();
  // Doit être différent du nom prérempli ET du nom original ET non vide.
  const canSubmit =
    trimmed.length > 0 &&
    trimmed !== defaultName.trim() &&
    trimmed !== (type?.name || "").trim() &&
    !saving;

  const submit = async () => {
    if (!type || !canSubmit) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/document-types/${type.id}/duplicate`, {
        name: trimmed,
      });
      toast.success(`« ${data.name} » créé`, {
        description: "Le nouveau type est prêt à être personnalisé",
      });
      onOpenChange(false);
      onDuplicated?.(data);
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      toast.error("Duplication impossible", { description: detail });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !saving && onOpenChange(v)}>
      <DialogContent className="max-w-[520px]" data-testid="duplicate-type-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[15px]">
            <Copy size={15} style={{ color: "#5A9EE0" }} />
            Dupliquer « {type?.name || "—"} »
          </DialogTitle>
          <DialogDescription className="text-[12.5px] pt-1" style={{ color: "#8B8F94" }}>
            Un nouveau type sera créé avec la même configuration
            (étapes, champs, délais).
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 mb-1">
          <label className="block text-[10px] uppercase tracking-widest mb-1.5" style={{ color: "#8B8F94" }}>
            Nom du nouveau type
          </label>
          <Input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={defaultName}
            data-testid="duplicate-type-name-input"
            onKeyDown={(e) => {
              if (e.key === "Enter" && canSubmit) submit();
            }}
          />
          <div className="text-[11px] mt-1.5" style={{ color: "#6B7280" }}>
            {trimmed === defaultName.trim()
              ? "Modifiez le nom prérempli pour activer la duplication."
              : trimmed.length === 0
                ? "Le nom ne peut pas être vide."
                : trimmed === (type?.name || "").trim()
                  ? "Le nom doit être différent du type original."
                  : "Prêt à dupliquer."}
          </div>
        </div>

        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="duplicate-type-cancel">
            Annuler
          </Button>
          <Button
            onClick={submit}
            disabled={!canSubmit}
            data-testid="duplicate-type-submit"
            className="text-white border-0"
            style={{ background: canSubmit ? "#0078D4" : "#3A3A3A" }}
          >
            <Copy size={13} className="mr-1" />
            {saving ? "Duplication…" : "Dupliquer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
